import React, { useState, useEffect } from 'react';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { withErrorBoundary, Attribution } from '@confluence/error-boundary';
import { BannerStateContainer } from '@confluence/banners';
import { useIsProductAdmin } from '@confluence/current-user';
import { fg } from '@confluence/feature-gating';

import {
	BANNER_HEIGHT,
	LivePagesInProductPromptGlobalBanner,
} from './LivePagesInProductPromptGlobalBanner';

export const BANNER_NAME = 'LivePagesInProductPromptGlobalBanner';

type LivePagesGlobalBannerContainerComponentProps = {
	bannerState: BannerStateContainer;
};

const LivePagesGlobalBannerContainerComponent = ({
	bannerState,
}: LivePagesGlobalBannerContainerComponentProps) => {
	const [shouldShowBanner, setShouldShowBanner] = useState<boolean>(false);
	const [isDismissed, setDismissed] = useState<boolean>(false);

	const { isProductAdmin } = useIsProductAdmin();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOnDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'banner',
				actionSubjectId: 'livePagesInProductPromptGlobalBanner',
				source: 'livePagesInProductPromptGlobalBanner',
			},
		}).fire();

		setDismissed(true);
	};

	useEffect(() => {
		// TODO: MODES-5422 Only show banner to unenrolled site admins, at least 24 hours after Opt-in modal is dismissed
		setShouldShowBanner(
			!isDismissed && // Banner is not dismissed
				!bannerState.state.banners.some((banner) => banner.name !== BANNER_NAME) && // No other banners are showing
				isProductAdmin && // User is a product admin
				fg('live_pages_in_product_prompt_global_banner'), // Feature gate is enabled
		);
	}, [isDismissed, bannerState.state.banners, isProductAdmin]);

	useEffect(() => {
		if (shouldShowBanner) {
			bannerState.show(BANNER_NAME, BANNER_HEIGHT);
		} else {
			bannerState.hide(BANNER_NAME);
		}
	}, [shouldShowBanner, bannerState]);

	return shouldShowBanner ? (
		<LivePagesInProductPromptGlobalBanner onDismiss={handleOnDismiss} />
	) : null;
};

export const LivePagesInProductPromptGlobalBannerContainer = withErrorBoundary({
	attribution: Attribution.PAGE_MODES,
})(() => (
	<Subscribe to={[BannerStateContainer]}>
		{(bannerState: BannerStateContainer) => (
			<LivePagesGlobalBannerContainerComponent bannerState={bannerState} />
		)}
	</Subscribe>
));
